import { GetIssueResponseDto } from '@issuemanager/api-client';
import { ExtendedTypography } from '@issuemanager/apps/client/components';
import { useUserDateRenderer } from '@issuemanager/apps/client/hooks';
import { useAtlasSemanticThemeTokens } from '@issuemanager/apps/client/theme';
import { Alert, AlertTitle, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  issue: GetIssueResponseDto;
  isOverdue?: boolean;
}

export function SlaBanner({ issue, isOverdue = false }: Props) {
  const semantic = useAtlasSemanticThemeTokens();
  const dateRenderer = useUserDateRenderer();
  const { t } = useTranslation('issue');
  const isSLADeactivated = !issue.slaEnabled;

  const { severity, title } = useMemo((): {
    severity: 'error' | 'warning' | 'success';
    title: string;
  } => {
    if (isOverdue) {
      return {
        severity: 'error',
        title: t('view.sla.banner.overdueSLA'),
      };
    }

    if (isSLADeactivated) {
      return {
        severity: 'warning',
        title: t('view.sla.banner.deactivatedBy'),
      };
    }

    return {
      severity: 'success',
      title: t('view.sla.banner.activatedBy'),
    };
  }, [isOverdue, isSLADeactivated]);

  return (
    <Alert severity={severity} aria-live='polite' variant='outlined'>
      <AlertTitle>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          mr={4}
        >
          <Typography fontWeight={semantic.fontWeight.emphasis.value}>
            {title}
          </Typography>
        </Stack>
        {!isOverdue ? (
          <Stack direction='row' alignItems='baseline' columnGap={1}>
            <ExtendedTypography
              variant='textMd'
              fontWeight={900}
              color={semantic.color.link.default.value}
            >
              {issue.slaStateChangedByName}
            </ExtendedTypography>
            <Typography color={semantic.color.text.body.value}>
              {issue.slaStateReason}
            </Typography>

            <ExtendedTypography
              variant='labelSm'
              color={semantic.color.text.body.value}
            >
              {dateRenderer.renderDateTime(issue.slaStateChangedAt)}
            </ExtendedTypography>
          </Stack>
        ) : (
          <Typography color={semantic.color.text.body.value}>
            {t('view.sla.banner.overdueSLAMessage')}
          </Typography>
        )}
      </AlertTitle>
    </Alert>
  );
}
